import ModalForm from '@/gf/components/ModalForm'
import PriceField from '@/gf/components/PriceField'
import SelectField from '@/gf/components/SelectField'
import TextField from '@/gf/components/TextField'
import useMsgs from '@/gf/hooks/useMsgs'
import Money from '@/gf/modules/Money'
import { gql, useMutation } from '@apollo/client'
import { yupResolver } from '@/gf/lib/yupResolver'
import { GraphQLError } from 'graphql'
import { useForm, useWatch } from 'react-hook-form'
import * as yup from 'yup'

const ADD_OFFLINE_TRANSACTION = gql`
  mutation AddOfflineTransaction(
    $supplier: String
    $dollarAmount: MoneyInput
    $action: OfflineTransactionType
    $storeOrderId: ID!
  ) {
    addOfflineTransaction(
      supplier: $supplier
      dollarAmount: $dollarAmount
      action: $action
      storeOrderId: $storeOrderId
    ) {
      id
    }
  }
`
type OfflineTransactionForm = {
  supplier: string
  dollarAmount: string
  action: 'payout' | 'supplier_credit'
}
const offlineTransactionSchema = yup.object({
  supplier: yup.string().ensure(),
  dollarAmount: yup.number().required().default(0),
  action: yup.string().oneOf(['payout', 'supplier_credit']).required(),
})

const failureMessage = 'Error adding offline supply'

export default function OfflineTransactionForm({
  onClose,
  onSubmit: _onSubmit,
  open,
  storeOrderId,
}: {
  storeOrderId: string
  onClose: () => void
  onSubmit: () => void
  open: boolean
}) {
  const offlineTransactionForm = useForm<OfflineTransactionForm>({
    defaultValues: {
      supplier: '',
      dollarAmount: '0',
      action: 'payout',
    },
    resolver: yupResolver(offlineTransactionSchema),
  })
  const selectedChargeType = useWatch({
    control: offlineTransactionForm.control,
    name: 'action',
  })
  const action = useWatch({
    control: offlineTransactionForm.control,
    name: 'action',
  })

  // The modal stays mounted, so clear the form on close: the next open starts from the defaults.
  // Do not use `shouldUnregister` for this: the select is not a registered field, and
  // react-hook-form 7.89 then leaves it out of getValues() (GEA-11417).
  const close = () => {
    offlineTransactionForm.reset()
    onClose()
  }

  const [addOfflineTransaction] = useMutation(ADD_OFFLINE_TRANSACTION)
  const [_msgs, msgsMgr] = useMsgs()
  const dollarAmountLabel = action === 'payout' ? 'Payout Amount' : 'Supplier Credit Amount'

  return (
    <ModalForm
      title="Add Offline Supply"
      onClose={close}
      onSubmit={(e) => {
        e.preventDefault()

        function mapFormToGqlVariables(form: OfflineTransactionForm) {
          return {
            ...form,
            storeOrderId,
            action: form.action.toLocaleUpperCase(),
            dollarAmount: Money.toPayload(Money.fromString(form.dollarAmount, 'USD')),
          }
        }

        const form = offlineTransactionForm.getValues()
        const variables = mapFormToGqlVariables(form)

        addOfflineTransaction({ variables })
          .then(() => {
            msgsMgr.add('Successfully added offline supply!', 'positive')
            close()
          })
          .catch((err: GraphQLError) => {
            msgsMgr.add(err.message || failureMessage, 'negative')
          })
      }}
      open={open}
    >
      <div className="mb-8 pt-4">
        <SelectField
          label="Charge Type"
          required
          options={[
            { id: 'payout', display: 'Payout' },
            { id: 'supplier_credit', display: 'Supplier Credit' },
          ]}
          currentId={selectedChargeType}
          onChange={(selectedId) => {
            if (selectedId === 'payout' || selectedId === 'supplier_credit') {
              offlineTransactionForm.setValue('action', selectedId)
            }
          }}
        />
        <PriceField
          label={dollarAmountLabel}
          errors={offlineTransactionForm.formState.errors.dollarAmount?.message}
          {...offlineTransactionForm.register('dollarAmount')}
        />
        <TextField
          label="Supplier of Transaction"
          errors={offlineTransactionForm.formState.errors.supplier?.message}
          {...offlineTransactionForm.register('supplier')}
        />
      </div>
    </ModalForm>
  )
}
